import { Grid, makeStyles } from "@material-ui/core";
import {
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Webcam from "react-webcam";
import styled from "styled-components";
import { mixed } from "yup";
import FaceIllustration from "../../../../assets/face-ok.jpg";
import { PrimaryButton } from "../../../../components/button";
import { FileField } from "../../../../components/forms/file-3";
import { Icon } from "../../../../components/icons";
import { CenteredFlex, GridItem } from "../../../../components/layout";
import { ConfigContext } from "../../../../pages/token-onboarding/single/config-context";
import { dataURLtoFile } from "../../../../utils/utils";
import { Field } from "../internal/field";
import { RenderFuncProps, Wrapper } from "./common";

const VideoWrapper = styled.div<{ hidden?: boolean }>`
  ${CenteredFlex};
  ${({ hidden }) => (hidden ? "display: none;" : "")};
  border-radius: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  padding: 0.5em;
  position: relative;
`;

const useStyles = makeStyles({
  video: {
    borderRadius: 10,
    width: "100%",
    height: "auto",
    maxWidth: 640,
  },
});

const ButtonWrapper = styled.div`
  ${CenteredFlex};
  position: absolute;
  bottom: 2em;
  width: 4em;
  height: 4em;
  border-radius: 100%;
  left: calc(50% - 1.5em);
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const Button = styled.button`
  ${CenteredFlex};
  font-size: inherit;
  background-color: rgba(255, 255, 255, 0.3);
  width: 2.7em;
  height: 2.7em;
  border: none;
  border-radius: 100%;
  cursor: pointer;

  .icon-primary {
    fill: rgba(0, 0, 0, 0.3);
  }

  &:hover,
  &:active {
    background-color: rgba(255, 255, 255, 0.5);
    .icon-primary {
      fill: rgba(0, 0, 0, 0.5);
    }
  }
`;

const StyledIcon = styled(Icon)`
  font-size: 1.4em;
`;

const FlipButtonWrapper = styled(ButtonWrapper)`
  font-size: 0.8em;
  bottom: 3em;
  left: calc(50% + 5em);
`;

const MirrorButtonWrapper = styled(FlipButtonWrapper)`
  right: calc(50% + 5em);
  left: auto;
`;

export const InnerFaceAttachment: FC<{
  path: string;
  consentNote?: string | null;
}> = ({ path, consentNote }) => {
  const { t } = useTranslation("fields");
  const classes = useStyles();
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: path,
  });

  const [showCamera, setShowCamera] = useState(false);
  const [deviceId, setDeviceId] = useState<number>(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [mirrored, setMirrored] = useState<boolean>(false);

  const filePicker = useRef<FileField>(null);
  const webcamRef = useRef<Webcam>(null);

  const startCamera = useCallback(() => {
    setShowCamera(true);
  }, []);

  const flip = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDeviceId((x) => ++x % devices.length);
    },
    [devices.length, setDeviceId]
  );

  const mirror = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMirrored((x) => !x);
    },
    [setMirrored]
  );

  const capture = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (webcamRef.current) {
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc) field.onChange(dataURLtoFile(imageSrc, "face.png"));
        setShowCamera(false);
      }

      // Push to end of stack so that React re-renders the field
      setTimeout(() => {
        if (filePicker.current) {
          filePicker.current.reMount();
        }
      }, 0);
    },
    [webcamRef, filePicker.current]
  );

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
      const availDevices = mediaDevices.filter(
        ({ kind }) => kind === "videoinput"
      );
      if (availDevices.length > 0) setShowCamera(true);
      setDevices(availDevices);
    });
  }, [setDevices, navigator.mediaDevices]);

  return (
    <Grid container spacing={3}>
      {consentNote ? (
        <>
          <GridItem top item xs={12}>
            {t("upload-a-photo-of-yourself")}
          </GridItem>
          <GridItem
            top
            item
            xs={12}
            dangerouslySetInnerHTML={{
              __html: t("selfie-list", {
                consentNote: consentNote,
              }),
            }}
          />
        </>
      ) : null}
      <GridItem top center xs={12}>
        {devices.length > 0 ? (
          <VideoWrapper hidden={!showCamera}>
            <Webcam
              className={classes.video}
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              mirrored={mirrored}
              videoConstraints={{ deviceId: devices[deviceId].deviceId }}
            />
            <MirrorButtonWrapper>
              <Button onClick={mirror}>
                <StyledIcon type="mirror" />
              </Button>
            </MirrorButtonWrapper>
            <ButtonWrapper>
              <Button onClick={capture}>
                <StyledIcon type="camera" />
              </Button>
            </ButtonWrapper>
            {devices.length > 1 ? (
              <FlipButtonWrapper>
                <Button onClick={flip}>
                  <StyledIcon type="flip" />
                </Button>
              </FlipButtonWrapper>
            ) : null}
          </VideoWrapper>
        ) : null}
        {!showCamera ? (
          <PrimaryButton
            icon="camera"
            onClick={startCamera}
            disabled={devices.length < 1}
          >
            {t("take-a-photo")}
          </PrimaryButton>
        ) : null}
      </GridItem>
      <GridItem top center xs={12}>
        {t("or")}
      </GridItem>
      <Grid item xs={12} md={6}>
        <img src={FaceIllustration} alt="face" />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileField
          {...field}
          ref={filePicker}
          label={t("upload-a-selfie")}
          error={!!error}
          helperText={error?.message}
        />
      </Grid>
    </Grid>
  );
};

const Elem: FC<RenderFuncProps> = (props) => {
  const { config } = useContext(ConfigContext);

  return (
    <Wrapper>
      <InnerFaceAttachment
        path={props.item.field}
        consentNote={config.configuration.consentNote}
      />
    </Wrapper>
  );
};

export const useFaceAttachmentField = () => {
  const { t } = useTranslation("fields");
  const schema = useMemo(
    () =>
      mixed()
        .test(
          "is-large",
          t(`file-size-cannot-exceed-6-mb`),
          (file: any) => !(file instanceof File) || file.size <= 6 * 1024 * 1024
        )
        .label(t("face-attachment-file")),
    [t]
  );

  return useMemo(
    () => ({
      schema,
      elem: Elem,
      service: "FACE" as "FACE",
    }),
    [schema]
  );
};
